import { useMemo } from 'react';
import { Inbox } from 'lucide-react';
import { TaskRow } from '@/components/TaskRow';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui';
import { useMe, useProjects, useTasks, useUpdateTask } from '@/lib/queries';

/**
 * Unscheduled work: assigned to me, still open, no due date.
 *
 * This exists because the data has a lot of it — tasks imported from Asana
 * without dates that then fall out of every date-driven view. The legacy app's
 * per-user "Inbox" projects (there are eleven of them) aren't the same thing
 * and are mostly empty.
 */
export function InboxPage() {
  const { data: me } = useMe();
  const { data: projects } = useProjects();
  const updateTask = useUpdateTask();

  const query = useTasks(
    {
      assigneeIds: me ? [me.id] : [],
      done: false,
      undated: true,
      sort: 'updated',
      order: 'desc',
      perPage: 500,
    },
    { enabled: me !== undefined },
  );

  const projectNames = useMemo(
    () => new Map((projects?.items ?? []).map((p) => [p.id, p.title])),
    [projects],
  );

  const tasks = query.data?.items ?? [];

  return (
    <div className="flex h-full flex-col">
      <header className="border-b line surface-card px-5 py-3">
        <h1 className="text-base font-semibold text-strong">Inbox</h1>
        <p className="text-xs text-muted">
          Offene Aufgaben ohne Fälligkeitsdatum
          {tasks.length > 0 && ` · ${tasks.length}`}
        </p>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {query.isPending ? (
          <SkeletonRows />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : tasks.length === 0 ? (
          <EmptyState
            icon={<Inbox />}
            title="Inbox ist leer"
            description="Alle dir zugewiesenen Aufgaben haben ein Fälligkeitsdatum."
          />
        ) : (
          <div className="divide-y line surface-card">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                projectName={projectNames.get(task.projectId)}
                onToggleDone={(t) => updateTask.mutate({ id: t.id, patch: { done: !t.done } })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
